// A38 — workspace presence heartbeat hook.
//
// Reports two timestamps to the backend:
//   - `last_active_at` (any UI event) — throttled to one POST per 30s.
//   - `last_input_at` (intentional input — keystroke, click, drawer
//     send) — POSTed immediately when fired, also rate-limited to one
//     per 5s to avoid hammering the backend on rapid typing.
//
// The backend resolver classifies the row into present_active /
// present_idle / absent based on threshold gaps; the human-reach
// watcher consults the resolver to decide whether to email
// out-of-band.

import { useEffect, useRef } from "react";
import { postWorkspacePresenceHeartbeat } from "../api/client";

const ACTIVE_THROTTLE_MS = 30_000;
const INPUT_THROTTLE_MS = 5_000;

export function usePresenceHeartbeat(workspaceId: number) {
  const lastActivePostRef = useRef(0);
  const lastInputPostRef = useRef(0);

  useEffect(() => {
    if (!workspaceId) return;

    function postActive() {
      const now = Date.now();
      if (now - lastActivePostRef.current < ACTIVE_THROTTLE_MS) return;
      lastActivePostRef.current = now;
      postWorkspacePresenceHeartbeat(workspaceId, false).catch(() => {});
    }

    function postInput() {
      const now = Date.now();
      if (now - lastInputPostRef.current < INPUT_THROTTLE_MS) return;
      lastInputPostRef.current = now;
      lastActivePostRef.current = now;
      postWorkspacePresenceHeartbeat(workspaceId, true).catch(() => {});
    }

    // Active triggers: anything that says the tab is in use.
    window.addEventListener("mousemove", postActive, { passive: true });
    window.addEventListener("scroll", postActive, { passive: true });
    window.addEventListener("focus", postActive);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") postActive();
    });

    // Input triggers: intentional engagement.
    window.addEventListener("keydown", postInput);
    window.addEventListener("click", postInput);

    // Fire one immediately on mount so the backend knows the Captain
    // is here as of now.
    postInput();

    return () => {
      window.removeEventListener("mousemove", postActive);
      window.removeEventListener("scroll", postActive);
      window.removeEventListener("focus", postActive);
      window.removeEventListener("keydown", postInput);
      window.removeEventListener("click", postInput);
    };
  }, [workspaceId]);
}
